import './export-all-modal.scss';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../../../store';
import { LogCriteria } from '@src/dtos/search-criteria.dto';
import { LogsType } from '@src/utils/enums';
import { trafficFilteringCriteriaConfig } from '../traffic-filtering-criteria-config';
import { urlFilteringCriteriaConfig } from '../url-filtering-criteria-config';
import { useDebouncedValidation } from '../../../../shared/hooks/debounced-validation-hook';
import { ExportAllModalFooter } from './export-all-modal-footer';
import { LogTypeCriteria } from './log-type-criteria';
import { SelectionOption } from '../../../../shared/selection-button/selection-option';
import usePromiseFeedback from '../../../../shared/promise-feedback/usePromiseFeedback';
import { EventType } from 'src/dtos/analytics.dto';
import { FwVersion } from 'src/dtos/version.dto';
import {
    getThreatFilteringCriteriaConfig
} from '@src/components/features/reports-and-logs/logs/threat-filtering-criteria-config';

interface ExportAllProps {
    onClose: () => void;
    logTypesToExport: LogsType[];
    activeVersion: FwVersion | undefined;
}

export const ExportAllModal: React.FC<ExportAllProps> = observer(({ onClose, logTypesToExport, activeVersion }) => {
    const { logsStore, notificationStore, customerStore } = useStores();
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();
    const [selectedLogsTypes, setSelectedLogsTypes] = useState(logTypesToExport);
    const [urlValidationErrors, setUrlValidationErrors] = useState<Map<string, string>>(new Map());
    const [trafficValidationErrors, setTrafficValidationErrors] = useState<Map<string, string>>(new Map());
    const [threatValidationErrors, setThreatValidationErrors] = useState<Map<string, string>>(new Map());
    
    // ...logsStore.searchCriteria define that for create new object as the searchCriteria but not change the store by ref
    const [searchCriteriaByType, setSearchCriteriaByType] = useState<Record<LogsType, LogCriteria>>({...logsStore.searchCriteria});

    const threatConfig = getThreatFilteringCriteriaConfig(customerStore.isAdvancedConnection);

    const debouncedValidateInputMap: Map<LogsType, any> = new Map([
        [LogsType.URL_FILTERING, useDebouncedValidation(urlFilteringCriteriaConfig, setUrlValidationErrors)],
        [LogsType.TRAFFIC, useDebouncedValidation(trafficFilteringCriteriaConfig(activeVersion), setTrafficValidationErrors)],
        [LogsType.THREAT, useDebouncedValidation(threatConfig, setThreatValidationErrors)]]);

    useEffect(() => {
        Object.values(logTypesToExport).forEach(type => {
            selectedLogsTypes?.includes(type) && validateInput(type);
        });
    }, [searchCriteriaByType, selectedLogsTypes]);

    const validateInput = (type: LogsType) => {
        const currentSearchCriteria = searchCriteriaByType[type];
        Object.keys(currentSearchCriteria).forEach(key => {
            debouncedValidateInputMap.get(type)?.(key, (currentSearchCriteria as any)[key]);
        });
    };

    const successCallback = (exportMethodName: string) => {
        onClose();
        void notificationStore.loadNotifications();
        logsStore.sendExportLogEvent(EventType.MultipleLogsExport, exportMethodName, new Map(Object.entries(searchCriteriaByType) as [LogsType, LogCriteria][]));
    };

    const onExport = (exportMethod: SelectionOption) => {
        promiseFeedbackFactory({
            promise: logsStore.downloadLogs(exportMethod.key, selectedLogsTypes, searchCriteriaByType),
            successCallback: () => successCallback(exportMethod.key),
            notifObj: { show: false }
        });
    };

    const toggleSelectedLogType = (type: LogsType) => {
        if (selectedLogsTypes?.includes(type)) {
            // remove type
            setSelectedLogsTypes([...selectedLogsTypes.filter(t => t !== type)]);
        } else {
            // add type
            setSelectedLogsTypes([...selectedLogsTypes, type]);
        }
    };

    const getValidationErrorsByType = (type: LogsType): Map<string, string> => {
        switch (type) {
            case LogsType.TRAFFIC:
                return trafficValidationErrors;
            case LogsType.URL_FILTERING:
                return urlValidationErrors;
            case LogsType.THREAT:
                return threatValidationErrors;
        }
    };

    const setSearchCriteria = (searchCriteria: LogCriteria, type: LogsType) => {
        searchCriteriaByType[type] = searchCriteria;
        setSearchCriteriaByType({ ...searchCriteriaByType });
    };


    return <div data-testid="export-all-modal">
        <div className="flex-column">
            <span>Select the logs you would like to export.</span>
            <span>It might take few minutes. You'll be notified once files are ready to view.</span>
        </div>
        <div className="logs-selection">
            {Object.values(logTypesToExport).map(type => <LogTypeCriteria logType={type} key={`export-${type}`}
                                                                          activeVersion={activeVersion}
                                                                          isChecked={selectedLogsTypes?.includes(type)}
                                                                          toggleIsChecked={() => toggleSelectedLogType(type)}
                                                                          setSearchCriteria={setSearchCriteria}
                                                                          validationErrors={getValidationErrorsByType(type)}
                                                                          searchCriteriaDto={searchCriteriaByType[type]}
                                                                          isAdvanced={customerStore.isAdvancedConnection}/>)}
        </div>
        <ExportAllModalFooter onClose={onClose} onExport={onExport}
            disabled={trafficValidationErrors.size > 0 || urlValidationErrors.size > 0 || threatValidationErrors.size > 0} />
        {promiseContextHolder}
    </div>;

});
